import timmafotos from "./timmafotos.json";

const PHOTO_URI_PREFIX = "https://hhrwwwphotos.blob.core.windows.net/wwwphotos";

export type TimmaServices = {
  subCategories: SubCategory[];
};

export type Photos = {
  [key: string]: string[];
};

const photos: Photos = timmafotos;

export const getRandomPhoto = () => {
  const photoKeys = Object.keys(photos);
  if (photoKeys.length === 0) return null;
  const randomKey = photoKeys[Math.floor(Math.random() * photoKeys.length)];
  const randomPhotos = photos[randomKey];
  if (randomPhotos && randomPhotos.length > 0) {
    return `${PHOTO_URI_PREFIX}/${
      randomPhotos[Math.floor(Math.random() * randomPhotos.length)]
    }`;
  }
  return null;
};

const injectPhotosToService = (service: Service) => {
  if (photos[service.serviceId]) {
    return {
      ...service,
      photoUris: photos[service.serviceId].map(
        (photo) => `${PHOTO_URI_PREFIX}/${photo}`,
      ),
    } as Service;
  }
  return service;
};

export const injectPhotos = (servicesResponse: ServicesResponse) => {
  const subCategories = servicesResponse.subCategories.map((subCategory) => {
    const services = subCategory.services.map((service) =>
      injectPhotosToService(service),
    );
    return {
      ...subCategory,
      services,
    } as SubCategory;
  });
  return {
    ...servicesResponse,
    subCategories,
  } as ServicesResponse;
};

export const fetchTimmaData = async (
  callback: (jsonData: ServicesResponse) => void,
  onError: (err: Error) => void,
) => {
  try {
    const res = await fetch(
      "https://varaa.timma.fi/api/customers/withservices/slug/highhairroomoy/public?subCategories=true",
    );
    if (res.ok) {
      res
        .json()
        .then((jsonData: ServicesResponse) => {
          callback(jsonData);
        })
        .catch((err) => {
          onError(new Error(err));
        });
    }
  } catch (e: any) {
    onError(new Error(e));
  }
};

export interface ServicesResponse {
  businessId: string;
  businessName: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  district: string;
  locationInfo: string;
  lat: number;
  lon: number;
  description: string;
  webpage: string;
  facebookPage: any;
  instagramPage: string;
  pinterestPage: any;
  twitterPage: any;
  forceMarketplacePrePayment: boolean;
  id: string;
  slug: string;
  gslug: string;
  rating: number[];
  cancellationTerms: string;
  publicSettings: PublicSettings;
  country: string;
  defaultLanguage: string;
  languages: string[];
  images: Image[];
  customerGroupName: any;
  customerGroupId: any;
  requireWebReservationAddress: any;
  requireWebReservationSSN: any;
  hasWebReservationPayment: boolean;
  hasGiftCardShop: boolean;
  hideFromMarketplace: boolean;
  logoUrl: string;
  cancellationPaymentPercent: number;
  hideRatingComments: boolean;
  userPriceInWebReservation: boolean;
  orderNumber: number;
  analyticsTrackingCode: any;
  pixelTrackingCode: any;
  hasMultiBooking: boolean;
  groupSalonGiftCards: any;
  requireWebPayment: boolean;
  webCancellationCloses: number;
  gtmTrackingCode: any;
  GA4TrackingCode: any;
  discardSalonClosedNotification: boolean;
  allowNonConnectMarketplacePrePayment: boolean;
  noOnlineBookingIndexing: boolean;
  createdAt: string;
  subCategories: SubCategory[];
  contracts: Contract[];
  paymentProvider: string;
}

export interface PublicSettings {
  noAnyUser: boolean;
  showImages: boolean;
  showPrices: boolean;
  onlyAnyUser: boolean;
  giftcardText: string;
  coronaMeasures: CoronaMeasures;
  showRealImages: boolean;
  maxTimeInterval: number;
  customCategories: CustomCategories;
  reservationColor: string;
  multipleLanguages: boolean;
  showAdditionalServicePrices: boolean;
}

export interface CoronaMeasures {
  equipmentDisinfection: boolean;
  touchPointsDisinfection: boolean;
  disposableEquipmentWashing: boolean;
  handWashingAndDisinfection: boolean;
}

export interface CustomCategories {
  "100": N100;
  "150": N150;
  "300": N300;
  "450": N450;
  "1200": N1200;
  "9500": N9500;
}

export interface N100 {
  icon: string;
  nameEn: string;
  nameEs: string;
  nameEt: string;
  nameFi: string;
  nameNo: string;
  nameSe: string;
  subCategoryId: number;
}

export interface N150 {
  icon: string;
  nameEn: string;
  nameEs: string;
  nameEt: string;
  nameFi: string;
  nameNo: string;
  nameSe: string;
  subCategoryId: number;
}

export interface N300 {
  icon: string;
  nameEn: string;
  nameEs: string;
  nameEt: string;
  nameFi: string;
  nameNo: string;
  nameSe: string;
  subCategoryId: number;
}

export interface N450 {
  icon: string;
  nameEn: string;
  nameEs: string;
  nameEt: string;
  nameFi: string;
  nameNo: string;
  nameSe: string;
  subCategoryId: number;
}

export interface N1200 {
  icon: string;
  nameEn: string;
  nameEs: string;
  nameEt: string;
  nameFi: string;
  nameNo: string;
  nameSe: string;
  subCategoryId: number;
}

export interface N9500 {
  icon: string;
  nameEn: string;
  nameEs: string;
  nameEt: string;
  nameFi: string;
  nameNo: string;
  nameSe: string;
  subCategoryId: number;
}

export interface Image {
  id: string;
  customerId: string;
  url: string;
  alt: string;
  orderNumber: number;
  createdAt: string;
  updatedAt: string;
}

export interface SubCategory {
  id: string;
  serviceId: number;
  maxServiceId: number;
  nextServiceId: number;
  name: string;
  description: string;
  appDescription?: string;
  nameSe: string;
  descriptionSe: string;
  appDescriptionSe: any;
  nameEn: string;
  descriptionEn: string;
  appDescriptionEn: any;
  nameNo: string;
  descriptionNo: string;
  appDescriptionNo: any;
  subCategoryId: number;
  price: any;
  duration?: number;
  appSort?: number;
  imageUrl?: string;
  isSubCategory: boolean;
  shortNameSe: any;
  shortNameEn: any;
  shortNameNo: any;
  nameFi: string;
  shortNameFi: any;
  descriptionFi: string;
  orderNumber: number;
  slugFi: any;
  slugSe: any;
  slugEn: any;
  slugNo: any;
  slugUmlautsFi: any;
  slugUmlautsSe: any;
  slugUmlautsEn: any;
  slugUmlautsNo: any;
  subCategorySlugFi: any;
  subCategorySlugSe: any;
  subCategorySlugEn: any;
  subCategorySlugNo: any;
  categoryOptional1Fi: any;
  categoryOptional1Se: any;
  categoryOptional1En: any;
  categoryOptional1No: any;
  categoryOptional2Fi: any;
  categoryOptional2Se: any;
  categoryOptional2En: any;
  categoryOptional2No: any;
  categoryOptional3Fi: any;
  categoryOptional3Se: any;
  categoryOptional3En: any;
  categoryOptional3No: any;
  consumerImageUrl?: string;
  isFavorite: any;
  isConsumerService: any;
  baseDescriptionFi: any;
  baseDescriptionSe: any;
  baseDescriptionEn: any;
  baseDescriptionNo: any;
  subCategoryIconPrimary?: string;
  subCategoryIconSecondary?: string;
  lengthIconPrimary: any;
  lengthIconSecondary: any;
  consumerCategoryNameFi?: string;
  consumerCategoryNameSe?: string;
  consumerCategoryNameEn?: string;
  consumerCategoryNameNo?: string;
  consumerCategoryOrder?: number;
  nameEs: string;
  shortNameEs: any;
  descriptionEs?: string;
  baseDescriptionEs: any;
  appDescriptionEs: any;
  consumerCategoryNameEs: any;
  subCategorySlugEs: any;
  slugEs: any;
  slugUmlautsEs: any;
  categoryOptional1Es: any;
  categoryOptional2Es: any;
  categoryOptional3Es: any;
  defaultReconnectCycleInWeeks: number;
  medianVisitInterval: any;
  nameRu: string;
  descriptionRu: string;
  appDescriptionRu: any;
  shortNameRu: any;
  baseDescriptionRu: any;
  consumerCategoryNameRu: any;
  nameEt: string;
  descriptionEt: string;
  appDescriptionEt: any;
  shortNameEt: any;
  baseDescriptionEt: any;
  consumerCategoryNameEt: any;
  slugEt: any;
  slugUmlautsEt: any;
  subCategorySlugEt: any;
  categoryOptional1Et: any;
  categoryOptional2Et: any;
  categoryOptional3Et: any;
  slugRu: any;
  slugUmlautsRu: any;
  subCategorySlugRu: any;
  categoryOptional1Ru: any;
  categoryOptional2Ru: any;
  categoryOptional3Ru: any;
  nameLv: string;
  descriptionLv: string;
  slugLv: any;
  shortNameLv: any;
  slugUmlautsLv: any;
  subCategorySlugLv: any;
  categoryOptional1Lv: any;
  categoryOptional2Lv: any;
  categoryOptional3Lv: any;
  orderCategoryId: number;
  services: Service[];
  additionalServices: any[];
}

export interface Service {
  customerId: string;
  serviceId: number;
  nameFi: string;
  descriptionFi?: string;
  nameSe?: string;
  descriptionSe?: string;
  nameEn: string;
  descriptionEn?: string;
  nameNo?: string;
  descriptionNo?: string;
  shortName: string;
  subCategoryId: number;
  price: number;
  duration: number;
  clientDuration: number;
  orderNumber: number;
  isShortCut: any;
  hideFromWebReservation: boolean;
  nameEs: string;
  descriptionEs?: string;
  reconnectCycleInWeeksEmail: any;
  activeInReconnectEmail: boolean;
  reconnectCycleInWeeksSms: any;
  activeInReconnectSms: boolean;
  vat?: number;
  color?: string;
  legacyServiceId: any;
  shortCutOrderNumber: any;
  nameRu?: string;
  descriptionRu?: string;
  nameEt?: string;
  descriptionEt?: string;
  countAsHaircutInAccounting: boolean;
  nameLv: any;
  descriptionLv: any;
  // custom field
  photoUris?: string[];
}

export interface Contract {
  businessName: string;
  businessId: string;
  address: string;
  email: string;
  clientRegisterRole: string;
}
